/**
 * Life4Earth V2 — Gantt Tasks API Adapter
 *
 * Endpoint: /api/GanttTasks
 *
 * Provides typed fetch functions and shape converters for the Gantt
 * schedule task data.
 *
 * Epic: DEV-396, Tasks: DEV-398/400
 */

import { apiFetch } from "./client";
import { normalizeGuid } from "../utils/guid";

// ── API Response Types ──────────────────────────────────────

export interface GanttTaskResponse {
  id: string;
  projectId: string;
  taskId: number | null;
  wbs: string;
  taskName: string;
  durationDays: number | null;
  startDate: string | null;
  endDate: string | null;
  percentComplete: number;
  predecessors: string | null;
  dependencyType: string;
  indentLevel: number;
  isMilestone: boolean;
  isSummary: boolean;
  isPinned: boolean;
  summaryLevel: string | null;   // 'phase' | 'highlight' | null — controls Summary tab surfacing
  assignedTo: string | null;
  notes: string | null;
  sortOrder: number;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
  // DEV-565: Critical path fields (read-only, from Recalculate)
  lateStart: string | null;
  lateFinish: string | null;
  totalFloat: number | null;
  isCritical: boolean;
}

export interface RecalculateApiResponse {
  tasks: GanttTaskResponse[];
  warnings: string[];
}

// ── Internal Type ───────────────────────────────────────────

export interface GanttTask {
  id: string;
  projectId: string;
  taskId: number;              // sequential activity number (1, 2, 3…)
  wbs: string;                 // structural hierarchy string ("1.0", "1.1.2") — optional
  taskName: string;
  durationDays: number | null;
  startDate: string | null;    // ISO date string "YYYY-MM-DD"
  endDate: string | null;
  percentComplete: number;
  predecessors: string;        // comma-separated taskId numbers, "" if none
  dependencyType: string;
  indentLevel: number;
  isMilestone: boolean;
  isSummary: boolean;
  isPinned: boolean;
  summaryLevel: string | null;   // 'phase' | 'highlight' | null
  assignedTo: string | null;
  notes: string;
  sortOrder: number;
  // DEV-565: Critical path fields (read-only)
  lateStart: string | null;
  lateFinish: string | null;
  totalFloat: number | null;
  isCritical: boolean;
}

// ── Shape Converter ─────────────────────────────────────────

export function apiResponseToGanttTask(r: GanttTaskResponse, index?: number): GanttTask {
  return {
    id: normalizeGuid(r.id),
    projectId: normalizeGuid(r.projectId),
    taskId: r.taskId ?? (index != null ? index + 1 : 0),
    wbs: r.wbs,
    taskName: r.taskName,
    durationDays: r.durationDays,
    startDate: r.startDate,
    endDate: r.endDate,
    percentComplete: r.percentComplete,
    predecessors: r.predecessors ?? "",
    dependencyType: r.dependencyType,
    indentLevel: r.indentLevel,
    // DEV-588: Fallback — if backend doesn't persist isMilestone yet,
    // auto-detect from durationDays === 0 (zero-duration = milestone).
    isMilestone: r.isMilestone || (r.durationDays === 0),
    isSummary: r.isSummary,
    isPinned: r.isPinned,
    summaryLevel: r.summaryLevel ?? null,
    assignedTo: r.assignedTo,
    notes: r.notes ?? "",
    sortOrder: r.sortOrder,
    // DEV-565: Critical path
    lateStart: r.lateStart ?? null,
    lateFinish: r.lateFinish ?? null,
    totalFloat: r.totalFloat ?? null,
    isCritical: r.isCritical ?? false,
  };
}

// ── API Functions ───────────────────────────────────────────

/** Fetch all Gantt tasks for a project, ordered by sort_order. */
export async function fetchGanttTasksByProject(
  projectGuid: string
): Promise<GanttTaskResponse[]> {
  return apiFetch<GanttTaskResponse[]>(
    `/api/GanttTasks?projectId=${encodeURIComponent(projectGuid)}`
  );
}

/** Create a new Gantt task. */
export async function createGanttTask(
  projectGuid: string,
  task: Omit<GanttTask, "id">
): Promise<GanttTaskResponse> {
  return apiFetch<GanttTaskResponse>(
    `/api/GanttTasks?projectId=${encodeURIComponent(projectGuid)}`,
    {
      method: "POST",
      body: JSON.stringify({
        taskId: task.taskId,
        wbs: task.wbs,
        taskName: task.taskName,
        durationDays: task.durationDays,
        startDate: task.startDate,
        endDate: task.endDate,
        percentComplete: task.percentComplete,
        predecessors: task.predecessors || null,
        dependencyType: task.dependencyType,
        indentLevel: task.indentLevel,
        isMilestone: task.isMilestone,
        isSummary: task.isSummary,
        isPinned: task.isPinned,
        summaryLevel: task.summaryLevel || null,
        assignedTo: task.assignedTo,
        notes: task.notes || null,
        sortOrder: task.sortOrder,
      }),
    }
  );
}

/** Update an existing Gantt task. */
export async function updateGanttTask(
  dbId: string,
  task: GanttTask
): Promise<GanttTaskResponse> {
  return apiFetch<GanttTaskResponse>(`/api/GanttTasks/${dbId}`, {
    method: "PUT",
    body: JSON.stringify({
      taskId: task.taskId,
      wbs: task.wbs,
      taskName: task.taskName,
      durationDays: task.durationDays,
      startDate: task.startDate,
      endDate: task.endDate,
      percentComplete: task.percentComplete,
      predecessors: task.predecessors || null,
      dependencyType: task.dependencyType,
      indentLevel: task.indentLevel,
      isMilestone: task.isMilestone,
      isSummary: task.isSummary,
      isPinned: task.isPinned,
      summaryLevel: task.summaryLevel || null,
      assignedTo: task.assignedTo,
      notes: task.notes || null,
      sortOrder: task.sortOrder,
    }),
  });
}

/**
 * Lightweight progress-only update.
 * Used by the Engagement role modal task peek.
 */
export async function patchGanttTaskProgress(
  taskId: string,
  percentComplete: number,
  notes?: string | null
): Promise<GanttTaskResponse> {
  return apiFetch<GanttTaskResponse>(`/api/GanttTasks/${taskId}/progress`, {
    method: "PATCH",
    body: JSON.stringify({
      percentComplete,
      ...(notes !== undefined ? { notes } : {}),
    }),
  });
}

/** Delete a Gantt task. */
export async function deleteGanttTask(taskId: string): Promise<void> {
  return apiFetch<void>(`/api/GanttTasks/${taskId}`, {
    method: "DELETE",
  });
}

/**
 * Batch upsert — full-replace all Gantt tasks for a project.
 * Tasks in the payload are created/updated; tasks not in the payload are deleted.
 */
export async function batchUpsertGanttTasks(
  projectGuid: string,
  tasks: GanttTask[]
): Promise<GanttTaskResponse[]> {
  return apiFetch<GanttTaskResponse[]>(
    `/api/GanttTasks/batch?projectId=${encodeURIComponent(projectGuid)}`,
    {
      method: "PUT",
      body: JSON.stringify({
        tasks: tasks.map((t) => ({
          id: t.id || undefined,
          taskId: t.taskId,
          wbs: t.wbs,
          taskName: t.taskName,
          durationDays: t.durationDays,
          startDate: t.startDate,
          endDate: t.endDate,
          percentComplete: t.percentComplete,
          predecessors: t.predecessors || null,
          dependencyType: t.dependencyType,
          indentLevel: t.indentLevel,
          isMilestone: t.isMilestone,
          isSummary: t.isSummary,
          isPinned: t.isPinned,
          summaryLevel: t.summaryLevel || null,
          assignedTo: t.assignedTo,
          notes: t.notes || null,
          sortOrder: t.sortOrder,
        })),
      }),
    }
  );
}

/**
 * Trigger forward scheduling — recalculates all task dates
 * from the project start date using the dependency graph.
 */
export async function recalculateGanttSchedule(
  projectGuid: string
): Promise<{ tasks: GanttTask[]; warnings: string[] }> {
  const response = await apiFetch<RecalculateApiResponse>(
    `/api/GanttTasks/recalculate?projectId=${encodeURIComponent(projectGuid)}`,
    { method: "POST" }
  );
  return {
    tasks: response.tasks.map((t, i) => apiResponseToGanttTask(t, i)),
    warnings: response.warnings,
  };
}
